import React, { useEffect, useState } from "react";
import {
  Box,
  CssBaseline,
  Grid,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  drawerWidth,
  GeneralAdminRoutes,
  mainLayoutStyles,
  showNotification,
} from "../../../../../constants";
import Header from "../../../../../common/Header";
import GeneralAdminSidebar from "../../../../../common/GeneralAdminSidebar";
import FilterModalComponent from "../../../../../common/FilterModalComponent";
import { getDateRange } from "../../../../../modules/OrgProf/hook/Organizationhook";
import { Toast } from "../../../../../common/alert";
import { useAuth } from "../../../../../hooks";
import { FilterSection, GroupsTable, SearchBar } from "../../components/components";
import { getAllGroups, getFilterGroups, groupsSearch, updateGroup } from "../../hooks/GAGroupsHooks";
import { getSingleUser } from "../../../../../modules/OrganizationQuickTales/hooks/QuickTalesHooks";

const GAGroupsContainer = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [selected, setSelected] = useState([]);
  const [statusFilterOptions, setStatusFilterOptions] = useState([]);
  const [creationDateFilterOptions, setCreationDateFilterOptions] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [groupsData, setGroupsData] = useState([]);

  const handleGetAllGroups = async () => {
    try {
      setLoading(true);
      const snap = await getAllGroups();
      const groupsRaw = snap?.docs?.map((d) => ({ id: d.id, ...d.data() })) ?? [];

      // hydrate createdBy -> createdByData so the table can show username like other GA tables
      const groups = await Promise.all(
        groupsRaw.map(async (g) => {
          if (!g?.createdBy || typeof g.createdBy !== "string") return g;
          try {
            const userSnap = await getSingleUser(g.createdBy);
            return {
              ...g,
              createdByData: userSnap?.data() ? userSnap.data() : null,
            };
          } catch (e) {
            return g;
          }
        })
      );
      setAllGroups(groups);
      setGroupsData(groups);
    } catch (error) {
      Toast("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleGetAllGroups();
  }, []);

  const handleFilterModalOpen = () => setFilterModal(true);
  const handleFilterModalClose = () => setFilterModal(false);

  const handleDeleteChip = (filterType, filterItem) => {
    if (filterType === "status") {
      setStatusFilterOptions((prev) => prev.filter((item) => item !== filterItem));
    } else if (filterType === "creationDate") {
      setCreationDateFilterOptions([]);
    }
  };

  useEffect(() => {
    handleFilterGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilterOptions, creationDateFilterOptions]);

  const handleFilterGroups = async () => {
    let dateRange;
    if (creationDateFilterOptions && creationDateFilterOptions.length > 0) {
      dateRange = getDateRange(creationDateFilterOptions);
    }
    setSelected([]);

    if (creationDateFilterOptions.length > 0 || statusFilterOptions.length > 0) {
      const temp = await getFilterGroups(
        dateRange,
        statusFilterOptions,
        setLoading,
        handleFilterModalClose
      );
      setGroupsData([...(temp || [])]);
    }

    if (creationDateFilterOptions.length === 0 && statusFilterOptions.length === 0) {
      setGroupsData(allGroups);
    }
  };

  const handleSearchGroups = async (e) => {
    try {
      const value = e.target.value;
      if (value.length >= 1) {
        const temp = await groupsSearch(allGroups, value);
        setGroupsData(temp);
      } else {
        setGroupsData(allGroups);
      }
    } catch (error) {
      console.log("error searching groups:", error.message);
    }
  };

  const resetFilter = async () => {
    setStatusFilterOptions([]);
    setCreationDateFilterOptions([]);
    setGroupsData(allGroups);
    setSelected([]);
  };

  const handleUpdateGroupStatus = async (status) => {
    try {
      setLoading(true);
      const patch = {
        status,
        updatedAt: new Date(),
        updatedBy: currentUser?.userName,
      };
      await Promise.all(selected.map((id) => updateGroup(id, patch)));
      await handleGetAllGroups();
      setSelected([]);
      showNotification("Group(s) updated successfully.");
    } catch (error) {
      Toast("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", width: `calc(100% - ${drawerWidth}px)` }}>
      <CssBaseline />
      <Header
        mainTitle={"Groups"}
        bgColor="#fff"
        buttonText={"Add Group"}
        handleSubmit={() => navigate(GeneralAdminRoutes.addGroup)}
      />
      <GeneralAdminSidebar />

      <Grid container style={{ ...mainLayoutStyles, background: "#fff", marginTop: 20 }}>
        <Grid container direction={"column"}>
          <FilterSection
            handleFilterModalOpen={handleFilterModalOpen}
            statusFilterOptions={statusFilterOptions}
            creationDateFilterOptions={creationDateFilterOptions}
            handleDeleteChip={handleDeleteChip}
          />
          <SearchBar
            selected={selected}
            setSelected={setSelected}
            placeholder={"Search Group Name or Group ID"}
            handleSearch={handleSearchGroups}
            handleUpdateGroupStatus={handleUpdateGroupStatus}
          />

          <GroupsTable
            selected={selected}
            setSelected={setSelected}
            groups={groupsData}
            onRowClick={(group) =>
              navigate(GeneralAdminRoutes.groupDetails, { state: { groupDetails: group } })
            }
          />
        </Grid>
      </Grid>

      <FilterModalComponent
        modalOpen={filterModal}
        handleClose={handleFilterModalClose}
        resetFilter={resetFilter}
        handleFilter={handleFilterGroups}
        data={[
          {
            mainOptions: creationDateFilterOptions,
            setMainOptions: setCreationDateFilterOptions,
            type: "date",
            title: "Creation Date",
            currentOptions: [
              { label: "All", selected: true },
              { label: "Today", selected: false },
              { label: "Past Week", selected: false },
              { label: "Last 30 Days", selected: false },
              { label: "Last 12 Months", selected: false },
              { label: "Enter a Range", selected: false },
            ],
          },
          {
            mainOptions: statusFilterOptions,
            setMainOptions: setStatusFilterOptions,
            type: "text",
            title: "Status",
            currentOptions: [
              { label: "All", selected: true },
              { label: "Active", selected: false },
              { label: "Inactive", selected: false },
            ],
          },
        ]}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
        onClick={() => setLoading(false)}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default GAGroupsContainer;
